import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plane,
  Train,
  Bus,
  Car,
  Home,
  Hotel,
  Utensils,
  MapPin,
  Ticket,
  ShoppingBag,
  Shield,
  Wallet,
  Calendar,
  Users,
  ArrowRight,
  ArrowLeft,
  PiggyBank,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Copy,
  Download,
  RefreshCw,
  Sparkles,
  Globe,
  CreditCard,
  Luggage,
  Smartphone,
  DollarSign,
  Target,
  Clock,
  ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

const TripCostCalculator = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tripData, setTripData] = useState({
    // Step 1: Trip Setup
    tripName: "",
    tripType: "vacation", // "home" or "vacation"
    fromCity: "",
    toCity: "",
    currency: "CAD",
    startDate: "",
    endDate: "",
    numberOfTravelers: 1,
    splitWithFriends: false,
    friendsSharing: 1,

    // Step 2: Transport
    transportMode: "flight", // flight, bus, train, car
    flightBaseFare: 0,
    flightTaxesFees: 0,
    airportTransfers: 0,
    busTrainFare: 0,
    carRentalTotal: 0,
    carFuelParking: 0,
    localTransit: 0,
    localRideshare: 0,

    // Step 3: Accommodation & Food
    accommodationType: "hotel", // hotel, hostel, airbnb, staying_with_family
    costPerNight: 0,
    numberOfNights: 1,
    shareAccommodation: false,
    peopleSharing: 1,
    foodBudgetMode: "moderate", // cheap, moderate, fancy
    dailyFoodBudget: 40,
    eatOutDays: 0,
    groceryDays: 0,

    // Step 4: Activities & Admin
    activitiesTotal: 0,
    shoppingSouvenirs: 0,
    visaFees: 0,
    travelInsurance: 0,
    baggageSeatFees: 0,
    simRoaming: 0,
    fxFeePercent: 2.5,
    bufferPercent: 10,

    // Step 5: Savings Plan
    existingSavings: 0,
    monthlySavings: 0,
  });

  const [showComparison, setShowComparison] = useState(false);
  const [comparisonScenario, setComparisonScenario] = useState("comfort");

  const steps = [
    { title: "Trip Setup", icon: MapPin },
    { title: "Transport", icon: Plane },
    { title: "Stay & Food", icon: Hotel },
    { title: "Extras & Fees", icon: Ticket },
    { title: "Your Plan", icon: PiggyBank },
  ];

  const currencies = [
    { code: "CAD", symbol: "$", name: "Canadian Dollar" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
    { code: "PHP", symbol: "₱", name: "Philippine Peso" },
    { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  ];

  const foodBudgetPresets = {
    cheap: { label: "Budget-friendly", daily: 25, description: "Street food, groceries, cooking" },
    moderate: { label: "Moderate", daily: 45, description: "Mix of eating out & cooking" },
    fancy: { label: "Treat yourself", daily: 80, description: "Restaurants & experiences" },
  };

  // Calculate trip duration
  const tripDuration = useMemo(() => {
    if (!tripData.startDate || !tripData.endDate) return 0;
    const start = new Date(tripData.startDate);
    const end = new Date(tripData.endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }, [tripData.startDate, tripData.endDate]);

  // Calculate months until trip
  const monthsUntilTrip = useMemo(() => {
    if (!tripData.startDate) return 0;
    const start = new Date(tripData.startDate);
    const today = new Date();
    const diffTime = start - today;
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    return Math.max(0, diffMonths);
  }, [tripData.startDate]);

  // Main calculation
  const calculations = useMemo(() => {
    const nights = tripData.numberOfNights || Math.max(0, tripDuration - 1);
    const days = tripDuration || tripData.numberOfNights + 1 || 1;

    // Transport
    let longDistanceTotal = 0;
    if (tripData.transportMode === "flight") {
      longDistanceTotal = (tripData.flightBaseFare || 0) + (tripData.flightTaxesFees || 0);
    } else if (tripData.transportMode === "bus" || tripData.transportMode === "train") {
      longDistanceTotal = tripData.busTrainFare || 0;
    } else if (tripData.transportMode === "car") {
      longDistanceTotal = (tripData.carRentalTotal || 0) + (tripData.carFuelParking || 0);
    }
    longDistanceTotal += tripData.airportTransfers || 0;

    const localTransportTotal = (tripData.localTransit || 0) + (tripData.localRideshare || 0);

    // Accommodation
    let accommodationGross = 0;
    if (tripData.accommodationType !== "staying_with_family") {
      accommodationGross = (tripData.costPerNight || 0) * nights;
    }
    const peopleSharing = tripData.shareAccommodation ? Math.max(1, tripData.peopleSharing) : 1;
    const accommodationPerPerson = accommodationGross / peopleSharing;

    // Food
    const foodTotal = (tripData.dailyFoodBudget || 0) * days;

    // Activities & Shopping
    const activitiesShoppingTotal = (tripData.activitiesTotal || 0) + (tripData.shoppingSouvenirs || 0);

    // Admin & Safety
    const adminSafetyTotal =
      (tripData.visaFees || 0) +
      (tripData.travelInsurance || 0) +
      (tripData.baggageSeatFees || 0) +
      (tripData.simRoaming || 0);

    // Subtotal
    const subtotal =
      longDistanceTotal +
      localTransportTotal +
      accommodationPerPerson +
      foodTotal +
      activitiesShoppingTotal +
      adminSafetyTotal;

    // FX Fee
    const fxFee = subtotal * ((tripData.fxFeePercent || 0) / 100);

    // Buffer
    const buffer = subtotal * ((tripData.bufferPercent || 0) / 100);

    // Total per person
    const totalPerPerson = subtotal + fxFee + buffer;

    // Group total (if splitting other costs)
    const friendsCount = tripData.splitWithFriends ? Math.max(1, tripData.friendsSharing) : 1;

    // Savings calculations
    const alreadyCovered = tripData.existingSavings || 0;
    const remainingToSave = Math.max(0, totalPerPerson - alreadyCovered);
    const months = monthsUntilTrip || 1;
    const requiredPerMonth = remainingToSave / months;
    const plannedPerMonth = tripData.monthlySavings || 0;

    // Status determination
    let status = "on_track";
    let statusColor = "emerald";
    let statusMessage = "";

    if (remainingToSave <= 0) {
      status = "covered";
      statusColor = "emerald";
      statusMessage = "You already have enough saved for this trip!";
    } else if (plannedPerMonth >= requiredPerMonth) {
      status = "on_track";
      statusColor = "emerald";
      statusMessage = `You're on track! Keep saving ${formatCurrency(plannedPerMonth)}/month.`;
    } else if (plannedPerMonth >= requiredPerMonth * 0.75) {
      status = "slightly_behind";
      statusColor = "amber";
      statusMessage = `You're slightly behind. Try saving ${formatCurrency(Math.ceil(requiredPerMonth - plannedPerMonth))} more per month.`;
    } else {
      status = "far_behind";
      statusColor = "red";
      statusMessage = `You'll need to increase savings or adjust your trip to meet your goal.`;
    }

    // Calculate when they'll be ready at current rate
    const monthsToReady = plannedPerMonth > 0 ? Math.ceil(remainingToSave / plannedPerMonth) : Infinity;
    const shortfall = plannedPerMonth > 0 && months > 0 ? remainingToSave - (plannedPerMonth * months) : remainingToSave;

    return {
      // Breakdown
      longDistanceTotal,
      localTransportTotal,
      accommodationGross,
      accommodationPerPerson,
      foodTotal,
      activitiesShoppingTotal,
      adminSafetyTotal,
      subtotal,
      fxFee,
      buffer,
      totalPerPerson,
      // Trip info
      nights,
      days,
      friendsCount,
      // Savings
      alreadyCovered,
      remainingToSave,
      requiredPerMonth,
      plannedPerMonth,
      monthsUntilTrip: months,
      monthsToReady,
      shortfall: Math.max(0, shortfall),
      // Status
      status,
      statusColor,
      statusMessage,
      // Weekly breakdown
      requiredPerWeek: requiredPerMonth / 4,
      plannedPerWeek: plannedPerMonth / 4,
    };
  }, [tripData, tripDuration, monthsUntilTrip]);

  const formatCurrency = (amount) => {
    const currency = currencies.find((c) => c.code === tripData.currency) || currencies[0];
    return `${currency.symbol}${Math.round(amount).toLocaleString()}`;
  };

  const updateField = (field, value) => {
    setTripData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCopyPlan = async () => {
    const planText = `
🌍 ${tripData.tripName || "My Trip"} - Budget Plan
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📍 ${tripData.fromCity} → ${tripData.toCity}
📅 ${tripData.startDate} to ${tripData.endDate} (${calculations.days} days)
👥 ${tripData.numberOfTravelers} traveler(s)

💰 COST BREAKDOWN (per person)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✈️ Transport: ${formatCurrency(calculations.longDistanceTotal + calculations.localTransportTotal)}
🏨 Accommodation: ${formatCurrency(calculations.accommodationPerPerson)}
🍽️ Food: ${formatCurrency(calculations.foodTotal)}
🎫 Activities & Shopping: ${formatCurrency(calculations.activitiesShoppingTotal)}
📋 Admin & Fees: ${formatCurrency(calculations.adminSafetyTotal)}
💱 FX Fees: ${formatCurrency(calculations.fxFee)}
🛡️ Buffer: ${formatCurrency(calculations.buffer)}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 TOTAL: ${formatCurrency(calculations.totalPerPerson)}

💾 SAVINGS PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Already saved: ${formatCurrency(calculations.alreadyCovered)}
Still need: ${formatCurrency(calculations.remainingToSave)}
Monthly target: ${formatCurrency(calculations.requiredPerMonth)}

Made with Compoundr 🌱
    `.trim();

    try {
      await navigator.clipboard.writeText(planText);
      toast.success("Plan copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy plan");
    }
  };

  const handleDownloadPlan = async () => {
    const element = document.getElementById("trip-results");
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const link = document.createElement("a");
      link.download = `${tripData.tripName || "trip"}-budget-plan.png`;
      link.href = canvas.toDataURL();
      link.click();
      toast.success("Plan downloaded!");
    } catch (err) {
      toast.error("Failed to download plan");
    }
  };

  const handleReset = () => {
    setTripData({
      tripName: "",
      tripType: "vacation",
      fromCity: "",
      toCity: "",
      currency: "CAD",
      startDate: "",
      endDate: "",
      numberOfTravelers: 1,
      splitWithFriends: false,
      friendsSharing: 1,
      transportMode: "flight",
      flightBaseFare: 0,
      flightTaxesFees: 0,
      airportTransfers: 0,
      busTrainFare: 0,
      carRentalTotal: 0,
      carFuelParking: 0,
      localTransit: 0,
      localRideshare: 0,
      accommodationType: "hotel",
      costPerNight: 0,
      numberOfNights: 1,
      shareAccommodation: false,
      peopleSharing: 1,
      foodBudgetMode: "moderate",
      dailyFoodBudget: 40,
      eatOutDays: 0,
      groceryDays: 0,
      activitiesTotal: 0,
      shoppingSouvenirs: 0,
      visaFees: 0,
      travelInsurance: 0,
      baggageSeatFees: 0,
      simRoaming: 0,
      fxFeePercent: 2.5,
      bufferPercent: 10,
      existingSavings: 0,
      monthlySavings: 0,
    });
    setCurrentStep(0);
    toast.success("Calculator reset!");
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  // Comparison scenarios
  const getScenarioMultiplier = (scenario) => {
    switch (scenario) {
      case "budget": return 0.65;
      case "comfort": return 1;
      case "premium": return 1.5;
      default: return 1;
    }
  };

  return (
    <div className="space-y-6" data-testid="trip-calculator">
      {/* Back button */}
      {onBack && (
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/tools'}
          className="text-slate-600 hover:text-navy-700"
          data-testid="back-to-plan-btn"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to All Tools
        </Button>
      )}

      {/* Header */}
      <div className="text-center">
        <Badge className="bg-blue-100 text-blue-700 mb-3">Student Travel Tool</Badge>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
          Trip Cost Calculator
        </h2>
        <p className="text-slate-600">
          Plan your trip budget and create a savings plan that works
        </p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}
          </span>
          <span className="text-blue-600 font-medium">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between">
          {steps.map((step, idx) => (
            <button
              key={step.title}
              onClick={() => setCurrentStep(idx)}
              className={`flex flex-col items-center gap-1 transition-colors ${
                idx <= currentStep ? "text-blue-600" : "text-slate-400"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  idx < currentStep
                    ? "bg-blue-600 text-white"
                    : idx === currentStep
                    ? "bg-blue-100 text-blue-600 ring-2 ring-blue-600"
                    : "bg-slate-100"
                }`}
              >
                {idx < currentStep ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
              </div>
              <span className="text-xs hidden sm:block">{step.title}</span>
            </button>
          ))}
        </div>
      </div>

      <Card className="border-0 shadow-floating">
        <CardContent className="pt-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Trip Setup */}
            {currentStep === 0 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-1">
                    Where are you going?
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Tell us about your trip so we can help you budget
                  </p>
                </div>

                {/* Trip Type */}
                <div className="space-y-3">
                  <Label>What kind of trip is this?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => updateField("tripType", "home")}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        tripData.tripType === "home"
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-blue-300"
                      }`}
                    >
                      <Home className="w-6 h-6 text-blue-600 mb-2" />
                      <p className="font-semibold">Going Home</p>
                      <p className="text-xs text-slate-500">Visiting family</p>
                    </button>
                    <button
                      onClick={() => updateField("tripType", "vacation")}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        tripData.tripType === "vacation"
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-blue-300"
                      }`}
                    >
                      <Globe className="w-6 h-6 text-blue-600 mb-2" />
                      <p className="font-semibold">Vacation</p>
                      <p className="text-xs text-slate-500">Trip with friends</p>
                    </button>
                  </div>
                </div>

                {/* Trip Name */}
                <div className="space-y-2">
                  <Label>Trip Name (optional)</Label>
                  <Input
                    placeholder="e.g., Spring Break 2025, Christmas at Home"
                    value={tripData.tripName}
                    onChange={(e) => updateField("tripName", e.target.value)}
                  />
                </div>

                {/* Cities */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From</Label>
                    <Input
                      placeholder="e.g., Toronto"
                      value={tripData.fromCity}
                      onChange={(e) => updateField("fromCity", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To</Label>
                    <Input
                      placeholder="e.g., Vancouver"
                      value={tripData.toCity}
                      onChange={(e) => updateField("toCity", e.target.value)}
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={tripData.startDate}
                      onChange={(e) => updateField("startDate", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={tripData.endDate}
                      onChange={(e) => updateField("endDate", e.target.value)}
                    />
                  </div>
                </div>

                {tripDuration > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-blue-700 font-medium">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {tripDuration} day{tripDuration !== 1 ? "s" : ""} trip
                      {monthsUntilTrip > 0 && ` • ${monthsUntilTrip} month${monthsUntilTrip !== 1 ? "s" : ""} away`}
                    </p>
                  </div>
                )}

                {/* Currency & Travelers */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select
                      value={tripData.currency}
                      onValueChange={(v) => updateField("currency", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Travelers</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[tripData.numberOfTravelers]}
                        onValueChange={(v) => updateField("numberOfTravelers", v[0])}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="font-mono text-blue-600 w-8 text-center">
                        {tripData.numberOfTravelers}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Split with friends */}
                {tripData.numberOfTravelers > 1 && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Splitting costs with friends?</Label>
                        <p className="text-xs text-slate-500">We&apos;ll calculate per-person costs</p>
                      </div>
                      <Switch
                        checked={tripData.splitWithFriends}
                        onCheckedChange={(v) => updateField("splitWithFriends", v)}
                      />
                    </div>
                    {tripData.splitWithFriends && (
                      <div className="space-y-2">
                        <Label className="text-sm">How many people sharing costs?</Label>
                        <Slider
                          value={[tripData.friendsSharing]}
                          onValueChange={(v) => updateField("friendsSharing", v[0])}
                          min={1}
                          max={10}
                          step={1}
                        />
                        <p className="text-center text-blue-600 font-medium">
                          {tripData.friendsSharing} people
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: Transport */}
            {currentStep === 1 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-1">
                    How are you getting there?
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Enter your transport costs (round trip)
                  </p>
                </div>

                {/* Transport Mode */}
                <div className="space-y-3">
                  <Label>Main Transport</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: "flight", icon: Plane, label: "Flight" },
                      { value: "train", icon: Train, label: "Train" },
                      { value: "bus", icon: Bus, label: "Bus" },
                      { value: "car", icon: Car, label: "Car" },
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        onClick={() => updateField("transportMode", mode.value)}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                          tripData.transportMode === mode.value
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-blue-300"
                        }`}
                      >
                        <mode.icon className="w-5 h-5" />
                        <span className="text-sm">{mode.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Flight costs */}
                {tripData.transportMode === "flight" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Plane className="w-4 h-4 text-blue-600" />
                        Base Fare (cost in $)
                      </Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.flightBaseFare || ""}
                        onChange={(e) => updateField("flightBaseFare", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Taxes & Fees (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.flightTaxesFees || ""}
                        onChange={(e) => updateField("flightTaxesFees", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                )}

                {/* Bus/Train costs */}
                {(tripData.transportMode === "bus" || tripData.transportMode === "train") && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {tripData.transportMode === "bus" ? (
                        <Bus className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Train className="w-4 h-4 text-blue-600" />
                      )}
                      {tripData.transportMode === "bus" ? "Bus" : "Train"} Fare (cost in $)
                    </Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={tripData.busTrainFare || ""}
                      onChange={(e) => updateField("busTrainFare", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                )}

                {/* Car costs */}
                {tripData.transportMode === "car" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-blue-600" />
                        Car Rental Total (cost in $)
                      </Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.carRentalTotal || ""}
                        onChange={(e) => updateField("carRentalTotal", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fuel & Parking (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.carFuelParking || ""}
                        onChange={(e) => updateField("carFuelParking", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                )}

                {/* Airport/Station Transfers */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Airport/Station Transfers (cost in $)
                  </Label>
                  <Input
                    type="number"
                    placeholder="Uber, taxi, shuttle to/from airport"
                    value={tripData.airportTransfers || ""}
                    onChange={(e) => updateField("airportTransfers", parseFloat(e.target.value) || 0)}
                  />
                </div>

                {/* Local Transport */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-navy-700">Local Transport (at destination)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Transit Passes/Day Passes (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.localTransit || ""}
                        onChange={(e) => updateField("localTransit", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Uber/Taxi/Rideshare (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.localRideshare || ""}
                        onChange={(e) => updateField("localRideshare", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                {/* Running total */}
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-blue-600 mb-1">Transport Total</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatCurrency(calculations.longDistanceTotal + calculations.localTransportTotal)}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 3: Accommodation & Food */}
            {currentStep === 2 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-1">
                    Where will you stay & eat?
                  </h3>
                  <p className="text-slate-500 text-sm">
                    These are usually the biggest trip expenses
                  </p>
                </div>

                {/* Accommodation Type */}
                <div className="space-y-3">
                  <Label>Accommodation Type</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: "hotel", label: "Hotel", icon: Hotel },
                      { value: "hostel", label: "Hostel", icon: Users },
                      { value: "airbnb", label: "Airbnb", icon: Home },
                      { value: "staying_with_family", label: "Family/Friends", icon: Home },
                    ].map((type) => (
                      <button
                        key={type.value}
                        onClick={() => updateField("accommodationType", type.value)}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                          tripData.accommodationType === type.value
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-blue-300"
                        }`}
                      >
                        <type.icon className="w-5 h-5" />
                        <span className="text-xs sm:text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Accommodation Costs */}
                {tripData.accommodationType !== "staying_with_family" && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Cost per Night (cost in $)</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={tripData.costPerNight || ""}
                          onChange={(e) => updateField("costPerNight", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Number of Nights</Label>
                        <Input
                          type="number"
                          placeholder={String(Math.max(0, tripDuration - 1))}
                          value={tripData.numberOfNights || ""}
                          onChange={(e) => updateField("numberOfNights", parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>

                    {/* Share accommodation */}
                    <div className="flex items-center justify-between py-2">
                      <div>
                        <Label>Sharing room with others?</Label>
                        <p className="text-xs text-slate-500">Split the accommodation cost</p>
                      </div>
                      <Switch
                        checked={tripData.shareAccommodation}
                        onCheckedChange={(v) => updateField("shareAccommodation", v)}
                      />
                    </div>

                    {tripData.shareAccommodation && (
                      <div className="space-y-2">
                        <Label className="text-sm">People sharing room</Label>
                        <Slider
                          value={[tripData.peopleSharing]}
                          onValueChange={(v) => updateField("peopleSharing", v[0])}
                          min={1}
                          max={6}
                          step={1}
                        />
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">
                            Total: {formatCurrency(calculations.accommodationGross)}
                          </span>
                          <span className="text-blue-600 font-medium">
                            Your share: {formatCurrency(calculations.accommodationPerPerson)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {tripData.accommodationType === "staying_with_family" && (
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-emerald-700">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Great! Staying with family/friends saves a lot!
                    </p>
                  </div>
                )}

                {/* Food Budget */}
                <div className="space-y-4">
                  <Label>Daily Food Budget</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(foodBudgetPresets).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => {
                          updateField("foodBudgetMode", key);
                          updateField("dailyFoodBudget", preset.daily);
                        }}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          tripData.foodBudgetMode === key
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-blue-300"
                        }`}
                      >
                        <p className="font-semibold text-sm">{preset.label}</p>
                        <p className="text-lg font-bold text-blue-600">
                          {formatCurrency(preset.daily)}/day
                        </p>
                        <p className="text-xs text-slate-500">{preset.description}</p>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Or set custom daily budget</Label>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[tripData.dailyFoodBudget]}
                        onValueChange={(v) => {
                          updateField("dailyFoodBudget", v[0]);
                          updateField("foodBudgetMode", "custom");
                        }}
                        min={10}
                        max={150}
                        step={5}
                        className="flex-1"
                      />
                      <span className="font-mono text-blue-600 w-20 text-right">
                        {formatCurrency(tripData.dailyFoodBudget)}/day
                      </span>
                    </div>
                  </div>
                </div>

                {/* Running totals */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-blue-600 mb-1">Accommodation</p>
                    <p className="text-xl font-bold text-blue-700">
                      {formatCurrency(calculations.accommodationPerPerson)}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-amber-600 mb-1">Food ({calculations.days} days)</p>
                    <p className="text-xl font-bold text-amber-700">
                      {formatCurrency(calculations.foodTotal)}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Activities & Admin */}
            {currentStep === 3 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-1">
                    Activities, Shopping & Fees
                  </h3>
                  <p className="text-slate-500 text-sm">
                    The extras that make trips memorable (and add up!)
                  </p>
                </div>

                {/* Activities & Shopping */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-purple-600" />
                      Activities & Attractions (cost in $)
                    </Label>
                    <Input
                      type="number"
                      placeholder="Tours, tickets, events"
                      value={tripData.activitiesTotal || ""}
                      onChange={(e) => updateField("activitiesTotal", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-pink-600" />
                      Shopping & Souvenirs (cost in $)
                    </Label>
                    <Input
                      type="number"
                      placeholder="Gifts, mementos"
                      value={tripData.shoppingSouvenirs || ""}
                      onChange={(e) => updateField("shoppingSouvenirs", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {/* Admin & Safety */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-navy-700 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Admin & Safety Costs
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Visa Fees (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.visaFees || ""}
                        onChange={(e) => updateField("visaFees", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Travel Insurance (cost in $)</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.travelInsurance || ""}
                        onChange={(e) => updateField("travelInsurance", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm flex items-center gap-1">
                        <Luggage className="w-3 h-3" />
                        Baggage & Seat Fees (cost in $)
                      </Label>
                      <Input
                        type="number"
                        placeholder="Checked bags, seat selection"
                        value={tripData.baggageSeatFees || ""}
                        onChange={(e) => updateField("baggageSeatFees", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm flex items-center gap-1">
                        <Smartphone className="w-3 h-3" />
                        SIM / Roaming (cost in $)
                      </Label>
                      <Input
                        type="number"
                        placeholder="eSIM, data plan"
                        value={tripData.simRoaming || ""}
                        onChange={(e) => updateField("simRoaming", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                {/* FX Fee & Buffer */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-slate-600" />
                      Currency Exchange Fee (%)
                    </Label>
                    <p className="text-xs text-slate-500">
                      Credit card FX fees are usually 2-3%
                    </p>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[tripData.fxFeePercent]}
                        onValueChange={(v) => updateField("fxFeePercent", v[0])}
                        min={0}
                        max={5}
                        step={0.5}
                        className="flex-1"
                      />
                      <span className="font-mono text-slate-600 w-12 text-right">
                        {tripData.fxFeePercent}%
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      Emergency Buffer (%)
                    </Label>
                    <p className="text-xs text-slate-500">
                      Extra cushion for unexpected costs
                    </p>
                    <div className="flex items-center gap-3">
                      <Slider
                        value={[tripData.bufferPercent]}
                        onValueChange={(v) => updateField("bufferPercent", v[0])}
                        min={0}
                        max={25}
                        step={5}
                        className="flex-1"
                      />
                      <span className="font-mono text-emerald-600 w-12 text-right">
                        {tripData.bufferPercent}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Running total */}
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-purple-600 mb-1">Extras & Fees Total</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {formatCurrency(
                      calculations.activitiesShoppingTotal +
                        calculations.adminSafetyTotal +
                        calculations.fxFee +
                        calculations.buffer
                    )}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 5: Savings Plan & Results */}
            {currentStep === 4 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
                id="trip-results"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-1">
                    Your Trip Budget & Savings Plan
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Here&apos;s what your {tripData.tripName || "trip"} will cost
                  </p>
                </div>

                {/* Grand Total */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6 text-white text-center">
                  <p className="text-blue-100 mb-1">Total Trip Cost (per person)</p>
                  <p className="text-4xl font-bold mb-2">
                    {formatCurrency(calculations.totalPerPerson)}
                  </p>
                  <div className="flex justify-center gap-4 text-sm text-blue-200">
                    <span>{calculations.days} days</span>
                    <span>•</span>
                    <span>{tripData.fromCity} → {tripData.toCity}</span>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <h4 className="font-semibold text-navy-700 mb-3">Cost Breakdown</h4>
                  {[
                    { label: "Transport", value: calculations.longDistanceTotal + calculations.localTransportTotal, icon: Plane, color: "blue" },
                    { label: "Accommodation", value: calculations.accommodationPerPerson, icon: Hotel, color: "purple" },
                    { label: "Food", value: calculations.foodTotal, icon: Utensils, color: "amber" },
                    { label: "Activities & Shopping", value: calculations.activitiesShoppingTotal, icon: Ticket, color: "pink" },
                    { label: "Admin & Fees", value: calculations.adminSafetyTotal, icon: Shield, color: "slate" },
                    { label: "FX Fees", value: calculations.fxFee, icon: CreditCard, color: "slate" },
                    { label: "Buffer", value: calculations.buffer, icon: Shield, color: "emerald" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                      <div className="flex items-center gap-2">
                        <item.icon className={`w-4 h-4 text-${item.color}-600`} />
                        <span className="text-slate-600">{item.label}</span>
                      </div>
                      <span className="font-mono font-semibold">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>

                {/* Savings Plan Inputs */}
                <div className="bg-emerald-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-emerald-700 flex items-center gap-2">
                    <PiggyBank className="w-5 h-5" />
                    Your Savings Plan
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Already saved for this trip</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.existingSavings || ""}
                        onChange={(e) => updateField("existingSavings", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Can save per month</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={tripData.monthlySavings || ""}
                        onChange={(e) => updateField("monthlySavings", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                {/* Savings Status */}
                <div
                  className={`rounded-xl p-4 ${
                    calculations.status === "covered" || calculations.status === "on_track"
                      ? "bg-emerald-50"
                      : calculations.status === "slightly_behind"
                      ? "bg-amber-50"
                      : "bg-red-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {calculations.status === "covered" || calculations.status === "on_track" ? (
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    ) : calculations.status === "slightly_behind" ? (
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p
                        className={`font-semibold ${
                          calculations.status === "covered" || calculations.status === "on_track"
                            ? "text-emerald-700"
                            : calculations.status === "slightly_behind"
                            ? "text-amber-700"
                            : "text-red-700"
                        }`}
                      >
                        {calculations.status === "covered"
                          ? "You're all set!"
                          : calculations.status === "on_track"
                          ? "You're on track!"
                          : calculations.status === "slightly_behind"
                          ? "Almost there!"
                          : "Let's adjust the plan"}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{calculations.statusMessage}</p>
                    </div>
                  </div>

                  {calculations.remainingToSave > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Still need</p>
                        <p className="font-bold text-navy-700">{formatCurrency(calculations.remainingToSave)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Monthly target</p>
                        <p className="font-bold text-blue-600">{formatCurrency(calculations.requiredPerMonth)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Weekly target</p>
                        <p className="font-bold text-blue-600">{formatCurrency(calculations.requiredPerWeek)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-xs text-slate-500">Time to trip</p>
                        <p className="font-bold text-navy-700">{calculations.monthsUntilTrip} mo</p>
                      </div>
                    </div>
                  )}

                  {calculations.shortfall > 0 && calculations.status !== "on_track" && calculations.status !== "covered" && (
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <p className="text-sm text-slate-600">
                        <strong>Gap:</strong> At your current rate, you&apos;ll be short by{" "}
                        <span className="text-red-600 font-semibold">{formatCurrency(calculations.shortfall)}</span>
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        <Sparkles className="w-4 h-4 inline text-amber-500 mr-1" />
                        <strong>Options:</strong> Save {formatCurrency(Math.ceil(calculations.requiredPerMonth - calculations.plannedPerMonth))} more/month,
                        reduce trip cost, or push date back
                      </p>
                    </div>
                  )}
                </div>

                {/* Student Tips */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Money-Saving Tips for Students
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      Book flights on Tues/Wed for lower prices; use Google Flights price alerts
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      Consider nearby airports, sometimes $100+ cheaper
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      Get a no-FX-fee credit card (Brim, Neo, Rogers) to save 2-3%
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      Buy eSIM data (Airalo, Holafly) instead of roaming, often 50% cheaper
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 justify-center">
                  <Button onClick={handleCopyPlan} variant="outline" className="flex items-center gap-2">
                    <Copy className="w-4 h-4" />
                    Copy Plan
                  </Button>
                  <Button onClick={handleDownloadPlan} variant="outline" className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                  <Button onClick={handleReset} variant="outline" className="flex items-center gap-2 text-slate-500">
                    <RefreshCw className="w-4 h-4" />
                    Start Over
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {currentStep < steps.length - 1 ? (
              <Button
                onClick={() => setCurrentStep((s) => s + 1)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleCopyPlan} className="bg-emerald-600 hover:bg-emerald-700">
                <Copy className="w-4 h-4 mr-2" />
                Copy My Plan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TripCostCalculator;
